import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { decodeJwt, scPing, meContext } from "@/lib/api";
import { parseKeyfile } from "@/lib/crypto";
import { setRuntimeKeyfile, getRuntimeKeyfile, updateRuntimeKeyfile, clearVault, needsUnlock } from "@/lib/vault";
import { tokGet, tokSet, tokDel, sessionState } from "@/lib/weblock";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(tokGet("lattice_user") || "null"); }
    catch { return null; }
  });
  const [ready, setReady] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    const token = tokGet("lattice_token");
    if (!token) { setReady(true); return; }
    const payload = decodeJwt(token);
    const expired = payload?.exp && payload.exp * 1000 < Date.now();
    if (!payload || expired) {
      tokDel("lattice_token");
      tokDel("lattice_user");
      clearVault();
      setUser(null);
    } else {
      setUser((prev) => prev || {
        lns: payload.sub, role: payload.role, tenant_id: payload.tenant_id,
      });
      // Encrypted keyfile present but not in RAM after reload → require unlock.
      // AUDIT v3.1.0: col doppio PIN attivo prima viene il PIN. Questa richiesta, mostrata
      // sopra il cancello, lo nasconderebbe — e nella sessione esca direbbe a chi guarda
      // che qui dentro c'è un account vero.
      if (needsUnlock() && sessionState() === "real") setLocked(true);
    }
    setReady(true);
  }, []);

  // Il PIN reale riapre la cassetta e rimette il file chiave in memoria: da quel momento
  // non c'è più niente da chiedere. Questo stato va riletto quando succede.
  useEffect(() => {
    const rileggi = () => {
      // La cassetta restituisce anche l'identità: senza questa riga la scheda resterebbe
      // "sbloccata ma anonima" fino al ricaricamento successivo.
      try { const u = JSON.parse(tokGet("lattice_user") || "null"); if (u) setUser(u); } catch { /* niente */ }
      setLocked(needsUnlock() && sessionState() === "real");
    };
    window.addEventListener("lattice:vault", rileggi);
    return () => window.removeEventListener("lattice:vault", rileggi);
  }, []);

  // Online presence: ping the sidecar while a session is active (suite-wide).
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const beat = () => { scPing().catch(() => {}); };
    beat();
    const id = setInterval(() => { if (alive) beat(); }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // Keep owner flag / license countdown / trial status fresh after login & refresh.
  useEffect(() => {
    const lns = user?.lns;
    if (!lns) return;
    let alive = true;
    meContext(lns).then((ctx) => {
      if (!alive || !ctx) return;
      setUser((prev) => {
        if (!prev) return prev;
        const next = { ...prev, is_owner: ctx.is_owner, license: ctx.license,
          must_rotate_key: ctx.must_rotate,
          trial: { is_trial: ctx.is_trial, expires_at: ctx.trial_expires_at, days_left: ctx.trial_days_left, company_name: ctx.company_name } };
        tokSet("lattice_user", JSON.stringify(next));
        return next;
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.lns]);

  const login = useCallback(async (token, u, keyfile) => {
    tokSet("lattice_token", token);
    tokSet("lattice_user", JSON.stringify(u));
    if (keyfile) setRuntimeKeyfile(keyfile); // persists for the tab session
    setLocked(false);
    setUser(u);
  }, []);

  // Re-carica il file chiave in questa scheda (verifica che corrisponda all'account in sessione).
  const unlock = useCallback(async (kf) => {
    const parsed = parseKeyfile(kf);
    if (!parsed) throw new Error("File chiave non valido o incompleto");
    if (user?.lns && parsed.lns !== user.lns) {
      throw new Error(`Questo file chiave è di ${parsed.lns}, diverso dall'account in sessione (${user.lns})`);
    }
    setRuntimeKeyfile(kf);
    setLocked(false);
  }, [user?.lns]);

  const logout = useCallback(() => {
    tokDel("lattice_token");
    tokDel("lattice_user");
    tokDel("lattice_sc_token");
    clearVault();
    setLocked(false);
    setUser(null);
  }, []);

  const getPrivateKey = useCallback(() => {
    const kf = getRuntimeKeyfile();
    return kf?.secret_key_hex || kf?.private_key || null;
  }, []);

  const updateUser = useCallback((u) => {
    setUser(u);
    tokSet("lattice_user", JSON.stringify(u));
  }, []);

  const setKeyfile = useCallback((kf) => {
    if (kf) updateRuntimeKeyfile(kf); // re-encrypts the vault if unlocked (key rotation)
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, locked, login, unlock, logout, getPrivateKey, updateUser, setKeyfile, isAuth: !!user }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
